import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import TechBaseButton from '../Button';
import TechBaseText from '../Text';
import _ from 'lodash';
import { numberWithCommas } from 'utils';
import { applyColor } from 'utils/apply-color';

const formatValue = (val, type) => {
  const formatType = type === 'toggle' ? 'number' : type;
  if (formatType === 'percentage') {
    return `${(Number(val) * 100).toFixed(2)}%`;
  } else if (formatType === 'number') {
    const numVal = Number(val);
    if (!Number.isInteger(numVal)) {
      return numberWithCommas(numVal.toFixed(2));
    }
    return numberWithCommas(numVal);
  } else {
    return val;
  }
};

const DraggableButton = ({ id, value, name, type, month, valueType, colors, onClickAction, enabled }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: type,
    item: { id, value, month },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div ref={drag} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <TechBaseButton
        key={`${id}-${enabled}`}
        enabled={enabled}
        item={{
          buttonType: 'default',
          size: 'small',
          style: {
            borderRadius: '8px',
            padding: '4px 12px',
            fontSize: '12px',
            transition: 'all 0.3s ease',
          },
          children: [
            {
              type: 'tooltip',
              content: formatValue(value, valueType),
              children: [
                {
                  type: 'text',
                  content: _.startCase(name),
                },
              ],
            },
          ],
        }}
        onClickAction={() => onClickAction(month, id)}
      />
    </div>
  );
};

const DropTarget = ({ onDrop, children, month, accept, colors }) => {
  const [, drop] = useDrop(() => ({
    accept: accept,
    canDrop: (item) => item.month === month,
    drop: (item) => onDrop(month, item.id, item.value),
  }));

  return (
    <div ref={drop} style={{ minHeight: '50px', padding: '10px', border: '1px dashed #ccc' }}>
      {children}
    </div>
  );
};

const TechBaseToggleCell = (props) => {
  const {
    value,
    month,
    label,
    marketingPromoLabel,
    clearanceLabel,
    onDropToClearance,
    onDropToMarketingPromo,
    valueType,
    colors,
    onToggle,
    enabledMap: fullEnabledMap,
    buttonDataKey, 
    buttonTextKey, 
    buttonValueKey, 
    buttonIdKey, 
    buttonKeysToDisplay, 
    dragDropType, 
    acceptDropTypes, 
    type,
    dataKey,
  } = props.item || {};

  if (valueType === 'number' || valueType === 'percentage') {
    return (
      <div style={{
        textAlign: 'right',
        fontWeight: 'bold',
        fontSize: '16px',
        color: applyColor('$info', colors),
        padding: '5px 10px',
      }}>
        {formatValue(value, valueType)}
      </div>
    );
  }

  let buttonsData = [];
  if (buttonDataKey && value && typeof value === 'object') {
    const data = value[buttonDataKey];
    if (Array.isArray(data)) {
      buttonsData = data.map(item => ({
        id: item[buttonIdKey],
        name: item[buttonTextKey],
        value: item[buttonValueKey],
      }));
    } else if (typeof data === 'object' && data !== null) {
      buttonsData = Object.keys(data).map(key => ({
        id: key,
        name: key,
        value: data[key],
      }));
    }
  } else if (typeof value === 'object' && value !== null) {
    buttonsData = Object.keys(value).map(key => ({
      id: key,
      name: key,
      value: value[key],
    }));
  }

  const total = buttonsData.reduce((sum, item) => {
    const numericValue = Number(item.value) || 0;
    return sum + (fullEnabledMap[`${month}-${item.id}`] ? numericValue : 0);
  }, 0);

  const renderButtons = (currentDragDropType) => (
    buttonsData.map(item => {
      const lookupKey = `${month}-${item.id}`
      const isEnabled = fullEnabledMap[`${month}-${item.id}`];
      const buttonProps = {
        key: `${item.id}-${isEnabled}`,
        onClickAction:() => {
   onToggle(month, item.id);
 },
        item: {
          buttonType: 'default',
          size: 'small',
          style: {
            borderRadius: '8px',
            padding: '4px 12px',
            fontSize: '12px',
            border: applyColor(isEnabled ? '1px solid $primary' : '1px solid #d9d9d9', colors),
            backgroundColor: applyColor(isEnabled ? '#ffffff' : '#f0f0f0', colors),
            color: applyColor(isEnabled ? '$primary' : '#595959', colors),
            transition: 'all 0.3s ease',
            boxShadow: applyColor(isEnabled ? '0 2px 8px $primary-shadow' : 'none', colors),
          },
          children: [
            {
              type: 'tooltip',
              content: formatValue(item.value, valueType),
              children: [
                {
                  type: 'text',
                  content: _.startCase(item.name),
                },
              ],
            },
          ],
        },
        colors: colors,
      };

      if (type === 'draggable-toggle') {
        return (
          <DraggableButton
            id={item.id}
            value={item.value}
            name={item.name}
            type={currentDragDropType}
            month={month}
            valueType={valueType}
            colors={colors}
            onClickAction={() => onToggle(month, item.id)}
            enabled={isEnabled}
          />
        );
      } else {
        return <TechBaseButton {...buttonProps} enabled={isEnabled} />;
      }
    })
  );

  const buttonContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px',
  };

  const totalTextStyle = {
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#333',
    marginTop: '8px',
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '10px' }}>
      <div style={buttonContainerStyle}>
        {renderButtons(dragDropType)}
      </div>
      <TechBaseText item={{ content: formatValue(total, valueType), style: totalTextStyle }} />
    </div>
  );

  if (type === 'draggable-toggle' && acceptDropTypes && acceptDropTypes.length > 0) {
    let onDropHandler;
    if (label === clearanceLabel) {
      onDropHandler = onDropToClearance;
    } else if (label === marketingPromoLabel) {
      onDropHandler = onDropToMarketingPromo;
    }

    return (
      <DropTarget onDrop={onDropHandler} month={month} accept={acceptDropTypes} colors={colors}>
        {content}
      </DropTarget>
    );
  }

  return content;
};

export default TechBaseToggleCell;
